import React, { useCallback, useEffect, useMemo, useState } from 'react';
import venueDashboardApiService from '../../../services/venueDashboardApi';
import ManageBookingDrawer from './ManageBookingDrawer';
import VenueMobileHeader from './VenueMobileHeader';
import { formatDate, parseDateOnly } from '../utils/dateUtils';

const renderStars = (rating) => {
  const n = Math.max(0, Math.min(5, Number(rating) || 0));
  return `${'★'.repeat(n)}${'☆'.repeat(5 - n)}`;
};

const formatSubmittedAt = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString([], { day: 'numeric', month: 'short', year: 'numeric' });
};

const truncateComment = (text, max = 120) => {
  const s = String(text || '').trim();
  if (!s) return '—';
  if (s.length <= max) return s;
  return `${s.slice(0, max).trim()}…`;
};

const FeedbackSection = ({
  venueId,
  venueDetails = null,
  bookingRules = null,
  bookings = [],
  onConfirmBooking,
  onPatchBookingLocal,
  onRetryBookings,
  onOpenMessages,
  onSectionChange
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [meta, setMeta] = useState({ count: 0, average_rating: null, feedback: [] });
  const [drawerBooking, setDrawerBooking] = useState(null);
  const [openingBookingId, setOpeningBookingId] = useState(null);

  const loadFeedback = useCallback(async () => {
    if (!venueId) return;
    setLoading(true);
    setError('');
    try {
      const data = await venueDashboardApiService.getVenueFeedback(venueId, { bookings });
      setMeta({
        count: data?.count ?? (data?.feedback?.length || 0),
        average_rating: data?.average_rating ?? null,
        feedback: Array.isArray(data?.feedback) ? data.feedback : [],
      });
    } catch (err) {
      setError(err?.message || 'Could not load feedback.');
      setMeta({ count: 0, average_rating: null, feedback: [] });
    } finally {
      setLoading(false);
    }
  }, [venueId, bookings]);

  useEffect(() => {
    loadFeedback();
  }, [loadFeedback]);

  const averageDisplay = useMemo(() => {
    if (meta.average_rating == null) return null;
    return Number(meta.average_rating).toFixed(1);
  }, [meta.average_rating]);

  const handleOpenBooking = async (bookingId) => {
    if (!bookingId || openingBookingId) return;
    setOpeningBookingId(bookingId);
    try {
      const booking = await venueDashboardApiService.getBookingById(bookingId);
      setDrawerBooking(booking);
    } catch (err) {
      setError(err?.message || 'Could not open booking.');
    } finally {
      setOpeningBookingId(null);
    }
  };

  return (
    <div className="vd-feedback">
      <VenueMobileHeader activeSection="feedback" onSectionChange={onSectionChange} />
      <div className="vd-feedback-panel">
        <div className="vd-feedback-header">
          <div>
            <h1 className="vd-feedback-title">Customer feedback</h1>
            <p className="vd-feedback-subtitle">
              Ratings and comments from organizers after their events.
            </p>
          </div>
          <button type="button" className="vd-feedback-refresh" onClick={loadFeedback} disabled={loading}>
            {loading ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>

        <div className="vd-feedback-summary">
          <div className="vd-feedback-stat">
            <span className="vd-feedback-stat-value">{meta.count}</span>
            <span className="vd-feedback-stat-label">Total responses</span>
          </div>
          <div className="vd-feedback-stat">
            <span className="vd-feedback-stat-value">
              {averageDisplay != null ? (
                <>
                  {averageDisplay}
                  <span className="vd-feedback-stat-stars" aria-hidden="true">
                    {' '}
                    ★
                  </span>
                </>
              ) : (
                '—'
              )}
            </span>
            <span className="vd-feedback-stat-label">Average rating</span>
          </div>
        </div>

        {loading ? (
          <div className="vd-feedback-state">
            <div className="loading-spinner" />
            <p>Loading feedback…</p>
          </div>
        ) : null}

        {!loading && error ? (
          <div className="vd-feedback-state vd-feedback-state--error">
            <p>{error}</p>
            <button type="button" className="vd-feedback-retry" onClick={loadFeedback}>
              Try again
            </button>
          </div>
        ) : null}

        {!loading && !error && meta.feedback.length === 0 ? (
          <div className="vd-feedback-state">
            <p className="vd-feedback-empty-title">No feedback yet</p>
            <p className="vd-feedback-empty-copy">
              After past events, send a feedback request from a booking&apos;s price details panel.
              Submitted ratings will appear here.
            </p>
          </div>
        ) : null}

        {!loading && !error && meta.feedback.length > 0 ? (
          <div className="vd-feedback-table-wrap">
            <table className="vd-feedback-table">
              <thead>
                <tr>
                  <th>Submitted</th>
                  <th>Event</th>
                  <th>Booking</th>
                  <th>Rating</th>
                  <th>Comment</th>
                  <th aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {meta.feedback.map((row) => (
                  <tr key={row.id || row.booking_id}>
                    <td>{formatSubmittedAt(row.submitted_at)}</td>
                    <td>
                      <div className="vd-feedback-event-cell">
                        <strong>{row.event_name || 'Event'}</strong>
                        <span>
                          {row.booking_date
                            ? formatDate(parseDateOnly(row.booking_date))
                            : '—'}
                        </span>
                      </div>
                    </td>
                    <td>
                      <code className="vd-feedback-ref">{row.booking_reference || '—'}</code>
                    </td>
                    <td>
                      <span
                        className="vd-feedback-rating"
                        aria-label={`${row.rating} out of 5 stars`}
                      >
                        {renderStars(row.rating)}
                        <span className="vd-feedback-rating-num">{row.rating}/5</span>
                      </span>
                    </td>
                    <td className="vd-feedback-comment-cell">{truncateComment(row.comment)}</td>
                    <td>
                      <button
                        type="button"
                        className="vd-feedback-view-btn"
                        disabled={openingBookingId === row.booking_id}
                        onClick={() => handleOpenBooking(row.booking_id)}
                      >
                        {openingBookingId === row.booking_id ? 'Opening…' : 'View booking'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </div>

      {drawerBooking ? (
        <ManageBookingDrawer
          booking={drawerBooking}
          venueId={venueId}
          venuePaymentLink={venueDetails?.payment_link || ''}
          bookingRules={bookingRules}
          onClose={() => setDrawerBooking(null)}
          onUpdateBookingStatus={onConfirmBooking}
          onBookingRefresh={onRetryBookings}
          onBookingLocalUpdate={onPatchBookingLocal}
          onOpenMessages={onOpenMessages}
        />
      ) : null}
    </div>
  );
};

export default FeedbackSection;
